// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Checkbox, Divider, Popover } from '@mui/material';
import { AppIcon, Heading } from '@shared/ui-kit';
import { type AdvisoryInstitution } from '@shared/data-access/types/brokerage-types';
import { type FC, useMemo, useState } from 'react';
import { Disclaimer } from '../disclaimer';
import { useTranslation } from 'react-i18next';
import { useCheckbox } from '@shared/ui-kit/hooks/use-checkbox';
import { useModal } from '@shared/ui-kit/hooks';
import { ShareStepAccountList } from './share-step-account-list';
import { ShareStepEmailForm } from './share-step-email-form';
import { useShareAccount } from '@shared/advisory/aggregation/components/share-institutions-button/use-share-account';

export const ShareInstitutionsButton: FC<{
  institutions: AdvisoryInstitution[];
}> = ({ institutions }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<'SELECTOR' | 'FORM'>('SELECTOR');

  const institutionGroups = useMemo(
    () =>
      institutions
        .filter(inst => inst.id !== 'ALL')
        .map(inst => ({
          id: inst.id,
          name: inst.name,
          logo_url: inst.logo_url,
          accounts: inst.products?.accounts || [],
        }))
        .filter(g => g.accounts.length > 0),
    [institutions],
  );

  const allIbans = useMemo(
    () => institutionGroups.flatMap(g => g.accounts.map(a => a.iban)),
    [institutionGroups],
  );

  const checkboxProps = useCheckbox(allIbans);
  const [expanded, setExpanded] = useState<string[]>([]);
  const sharingLogic = useShareAccount();

  const onClose = () => {
    setIsOpen(false);
    setCurrentStep('SELECTOR');
    sharingLogic.setEmail('');
  };

  const handleShare = async (targetEmail?: string) => {
    const success = await sharingLogic.handleShare(
      checkboxProps.selected,
      undefined,
      targetEmail,
    );
    if (success) onClose();
  };

  return (
    <>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        elevation={0}
        slotProps={{
          paper: {
            className: 'bg-page',
            sx: {
              minWidth: '380px',
              borderRadius: '12px',
              border: '1px solid rgba(0, 0, 0, 0.25)',
              boxShadow: 'none',
              marginTop: '4px',
            },
          },
        }}
      >
        <ShareModalContent
          currentStep={currentStep}
          onNext={() => setCurrentStep('FORM')}
          onPrev={() => setCurrentStep('SELECTOR')}
          institutionGroups={institutionGroups}
          allIbans={allIbans}
          checkboxProps={checkboxProps}
          expanded={expanded}
          setExpanded={setExpanded}
          sharingLogic={sharingLogic}
          onShare={handleShare}
        />
      </Popover>

      <Button
        variant="outlined"
        color="secondary"
        startIcon={<AppIcon icon="Share" color="inherit" size="lg" />}
        onClick={e => {
          setAnchorEl(e.currentTarget);
          setIsOpen(true);
        }}
        disabled={institutionGroups.length === 0}
      >
        {t('common.share', { ns: 'common' })}
      </Button>
    </>
  );
};

const ShareModalContent: FC<{
  currentStep: 'SELECTOR' | 'FORM';
  onNext: () => void;
  onPrev: () => void;
  institutionGroups: any[];
  allIbans: string[];
  checkboxProps: any;
  expanded: string[];
  setExpanded: any;
  sharingLogic: any;
  onShare: (email?: string) => void;
}> = ({
  currentStep,
  onNext,
  onPrev,
  institutionGroups,
  allIbans,
  checkboxProps,
  expanded,
  setExpanded,
  sharingLogic,
  onShare,
}) => {
  const { t } = useTranslation();
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [ModalRenderer, { onOpen: openDisclaimer, onClose: closeDisclaimer }] =
    useModal({ component: 'dialog' });

  const isSelector = currentStep === 'SELECTOR';

  return (
    <div className="flex h-[550px] w-[450px] flex-col overflow-hidden bg-page">
      <header className="shrink-0 px-6 pb-4 pt-8">
        <div className="flex items-center gap-2">
          {!isSelector && (
            <button
              onClick={onPrev}
              className="mr-1 rounded-full p-1 hover:bg-gray-200"
            >
              <AppIcon icon="ArrowLeft" size="xl" color="gray" />
            </button>
          )}
          <Heading
            title={
              isSelector
                ? t('aggregator.share-advisor', { ns: 'aggregator' })
                : t('aggregator.share-step-2-title', { ns: 'aggregator' })
            }
            variant="h2"
            className="!text-xl"
          />
        </div>
        {isSelector && (
          <div className="mt-4 flex items-center justify-between text-border-dark">
            <p className="text-md">
              {t('aggregator.share-step-1-title', { ns: 'aggregator' })}
            </p>
            <button
              onClick={
                checkboxProps.isAllSelected
                  ? checkboxProps.clearSelection
                  : checkboxProps.selectAll
              }
              className="text-md font-medium hover:underline"
            >
              {checkboxProps.isAllSelected
                ? t('aggregator.deselect-all', { ns: 'aggregator' })
                : t('aggregator.select-all', { ns: 'aggregator' })}
            </button>
          </div>
        )}
      </header>

      <Divider />

      <div className="flex-1 overflow-y-auto">
        {isSelector ? (
          <ShareStepAccountList
            institutionGroups={institutionGroups}
            selectedIbans={checkboxProps.selected}
            toggleCheckbox={checkboxProps.toggleCheckbox}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        ) : (
          <ShareStepEmailForm
            email={sharingLogic.email}
            setEmail={sharingLogic.setEmail}
            onShare={onShare}
            isSharing={sharingLogic.isSharing}
            advisors={sharingLogic.advisors}
            isLoadingAdvisors={sharingLogic.isLoadingAdvisors}
          />
        )}
      </div>

      {isSelector && (
        <footer className="shrink-0 border-t border-gray-200 bg-page px-6 py-4">
          <div className="text-md mb-4 text-border-dark">
            {t('aggregator.accounts-selected', {
              count: checkboxProps.selected.length,
              total: allIbans.length,
              ns: 'aggregator',
            })}
          </div>
          <div className="flex items-center justify-between">
            <div
              className="flex cursor-pointer items-center gap-2"
              onClick={() => setIsAgreementChecked(!isAgreementChecked)}
            >
              <Checkbox checked={isAgreementChecked} className="!p-0" />
              <span
                className="text-md font-medium text-border-dark hover:underline"
                onClick={e => {
                  e.stopPropagation();
                  openDisclaimer();
                }}
              >
                {t('common.sign-agreement', { ns: 'common' })}
              </span>
            </div>
            <Button
              variant="contained"
              color="secondary"
              disabled={checkboxProps.selected.length === 0 || !isAgreementChecked}
              onClick={onNext}
              className="!rounded-xl"
            >
              {t('common.continue', { ns: 'common' })}
            </Button>
          </div>
        </footer>
      )}

      <ModalRenderer maxWidth="sm" fullWidth>
        <div className="bg-page p-8">
          <div className="max-h-[600px] overflow-y-auto pr-2">
            <Disclaimer />
          </div>
          <footer className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-6">
            <Button
              variant="outlined"
              color="secondary"
              onClick={closeDisclaimer}
              className="!rounded-xl"
            >
              {t('common.cancel', { ns: 'common' })}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setIsAgreementChecked(true);
                closeDisclaimer();
              }}
              className="!rounded-xl"
            >
              {t('common.accept', { ns: 'common' })}
            </Button>
          </footer>
        </div>
      </ModalRenderer>
    </div>
  );
};
